/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as Ron from 'blb-ui-types/ron';
import { Box, Button, Text } from 'grommet';
import { CredentialTypeNames, UserCredentialStatus, UserCredentialType } from 'blb-constants/ron';

export const CredentialTypeList: UserCredentialType[] = [
  UserCredentialType.GENERAL_COMMISSION,
  UserCredentialType.RON_COMMISSION,
  UserCredentialType.CLOSING_AGENT_LICENSE,
  UserCredentialType.DIGITAL_CERTIFICATE,
  UserCredentialType.EO_INSURANCE,
  UserCredentialType.SIGNING_AGENT_CERTIFICATE,
  UserCredentialType.SURETY_BOND_CERTIFICATE,
  UserCredentialType.SEAL,
  UserCredentialType.SIGNATURE,
  UserCredentialType.INITIALS,
  UserCredentialType.BACKGROUND_CHECK,
];

const CredentialStatusTexts: Record<UserCredentialStatus, string> = {
  [UserCredentialStatus.NEW]: 'Uploaded',
  [UserCredentialStatus.APPROVED]: 'Approved',
  [UserCredentialStatus.REJECTED]: 'Rejected',
  [UserCredentialStatus.EXPIRED]: 'Expired',
};

const CredentialStatusColors: Record<UserCredentialStatus, string> = {
  [UserCredentialStatus.NEW]: 'sky',
  [UserCredentialStatus.APPROVED]: 'accentGreen',
  [UserCredentialStatus.REJECTED]: 'accentRed',
  [UserCredentialStatus.EXPIRED]: 'accentRed',
};

export namespace CredentialTable {
  export interface Props {
    credentials: Ron.API.UserCredential[];
    onReview: (credential: Ron.API.UserCredential) => void;
  }
}

export const getLatestCredentialsByType = (
  credentials: Ron.API.UserCredential[],
): {
  credential: Ron.API.UserCredential | undefined;
  credentialType: UserCredentialType;
}[] => {
  const sortedCredential = credentials.sort(
    (credential1, credential2) => credential2.createdAt.getTime() - credential1.createdAt.getTime(),
  );
  return CredentialTypeList.map(credentialType => {
    return { credential: sortedCredential.find(c => c.type === credentialType), credentialType };
  });
};

export const CredentialTable: React.FC<CredentialTable.Props> = ({ credentials, onReview }) => {
  const renderStatus = (credential?: Ron.API.UserCredential) => {
    let statusTxt: string;
    let statusColor: string;
    if (!credential) {
      statusTxt = 'Did not upload';
      statusColor = 'cloud70';
    } else {
      statusTxt = CredentialStatusTexts[credential.status];
      statusColor = CredentialStatusColors[credential.status];
    }
    return (
      <>
        <Box background={statusColor} height="8px" margin={{ right: 'xsmall' }} round="xxsmall" width="8px" />
        <Text>{statusTxt}</Text>
      </>
    );
  };
  const tableHeader = (
    <Box
      border={{ color: 'cloud50', side: 'bottom', size: 'xsmall' }}
      direction="row"
      pad={{ horizontal: 'large', vertical: 'small' }}
    >
      <Box basis="50%">
        <Text size="small" weight="bold">
          CREDENTIAL
        </Text>
      </Box>
      <Box>
        <Text size="small" weight="bold">
          STATUS
        </Text>
      </Box>
    </Box>
  );

  const latestCredentialsByTypes = getLatestCredentialsByType(credentials);

  return (
    <Box border={{ color: 'cloud70', size: 'xsmall' }} id="it-credentialTable" round="xsmall">
      {tableHeader}
      {latestCredentialsByTypes.map(({ credentialType, credential }) => {
        return (
          <Box
            align="center"
            border={{ color: 'cloud50', size: 'xsmall' }}
            data-it={`credentialTable-row-${credentialType}`}
            direction="row"
            id="credentialTable-row"
            key={credentialType}
            pad={{ horizontal: 'large', vertical: 'xsmall' }}
          >
            <Box basis="50%">
              <Text id="it-credentialTable-row-typeText">{CredentialTypeNames[credentialType]}</Text>
            </Box>
            <Box align="center" direction="row" flex="grow" id="it-credentialTable-row-status">
              {renderStatus(credential)}
            </Box>
            <Box direction="row" id="it-credentialTable-row-button" justify="end">
              <Text>
                <Button
                  disabled={!credential || credential.status === UserCredentialStatus.EXPIRED}
                  id={`it-credentialTable-button-${credentialType}`}
                  label="Review"
                  onClick={() => credential && onReview(credential)}
                  primary
                />
              </Text>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
